import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";

const CUSTOMER_PORTAL_SESSION_CREATE = gql`
  mutation CustomerPortalSessionCreate($params: CustomerPortalSessionInput!) {
    customerPortalSessionCreate(params: $params) {
      code
      data {
        url
        type
        customerId
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const SUBSCRIPTION_UPDATE = gql`
  mutation SubscriptionUpdate($params: SubscriptionUpdateInput!) {
    subscriptionUpdate(params: $params) {
      code
      data {
        appPlanCode
        appPlanPriceId
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const SUBSCRIPTION_CANCEL = gql`
  mutation SubscriptionCancel($params: SubscriptionCancelInput!) {
    subscriptionCancel(params: $params) {
      code
      data {
        periodEnd
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

/**
 * When the plan renews and what it will charge (owner, 2026-10-15) — ms_auth reads it live from Stripe: the period end
 * and the invoice PREVIEW (discounts, proration and tax included). Signed in; the caller's own account.
 */
const SUBSCRIPTION_NEXT_CHARGE = gql`
  query SubscriptionNextCharge {
    subscriptionNextCharge {
      code
      data { hasSubscription status renewsAt endsAt amount currencyCode interval discountName discountAmount discountEndsAt }
      errors { name errors warnings }
    }
  }
`;

export interface SubscriptionNextCharge {
  /** False on a free plan or an app trial: nothing renews. */
  hasSubscription?: boolean | null;
  /** Stripe's status: active, trialing, past_due, canceled… */
  status?: string | null;
  /** When the plan renews and the next charge is taken (ISO); null when it will not renew. */
  renewsAt?: string | null;
  /** Set when the plan is cancelled at the period end: it stops then, nothing more is charged (ISO). */
  endsAt?: string | null;
  /** Major units, discounts and tax included; null when unknown. */
  amount?: number | null;
  currencyCode?: string | null;
  /** month | year */
  interval?: string | null;
  /** The discount the next charge carries, by its Stripe name ("99% OFF OCT"); null when none. */
  discountName?: string | null;
  /** What it takes off the next charge, major units. */
  discountAmount?: number | null;
  /** When a limited discount stops (ISO); null for a lasting one. */
  discountEndsAt?: string | null;
}

export interface CustomerPortalSession {
  url?: string | null | undefined;
  type?: string | null | undefined;
  customerId?: string | null | undefined;
}

export interface CustomerPortalSessionInput {
  appPlanCode?: string | null | undefined;
  appPlanPriceId?: string | null | undefined;
  returnUrl?: string | null | undefined;
}

export interface SubscriptionUpdate {
  appPlanCode?: string | null | undefined;
}

export interface SubscriptionUpdateInput {
  appPlanCode: string;
  appPlanPriceId: string;
}

export interface SubscriptionCancel {
  periodEnd?: string | null | undefined;
}

export interface SubscriptionCancelInput {
  reason?: string | null | undefined;
}

/**
 * Customer Portal service that handles customer portal sessions and subscription management
 */
export class CustomerPortalService {
  /**
   * Create a new customer portal session
   */
  static async createSession(
    params: CustomerPortalSessionInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      CUSTOMER_PORTAL_SESSION_CREATE,
      {
        params,
      }
    );
    return result;
  }

  /**
   * Update subscription plan
   */
  static async updateSubscription(
    params: SubscriptionUpdateInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(SUBSCRIPTION_UPDATE, {
      params,
    });
    return result;
  }

  /**
   * Cancel subscription
   */
  /** When the signed-in account's plan renews and what it will charge. Always fresh: a change in Stripe moves it. */
  static async nextCharge(): Promise<OpResult> {
    return ApiService.executeQuery(SUBSCRIPTION_NEXT_CHARGE, {}, { fetchPolicy: "network-only" });
  }

  static async cancelSubscription(
    params: SubscriptionCancelInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(SUBSCRIPTION_CANCEL, {
      params,
    });
    return result;
  }
}
